import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { InsightCallout } from "../DataCards/InsightCallout";
import { OnboardingBox } from "./components/OnboardingBox";
import { ToeicLevelDetailsAccordion } from "./components/toeic-level-overview/ToeicLevelDetailsAccordion";
import type { ToeicLevelDetail } from "./components/toeic-level-overview/ToeicLevelDetailsAccordion";
import { ToeicLevelProgression } from "./components/toeic-level-overview/ToeicLevelProgression";
import type { ToeicLevelNode } from "./components/toeic-level-overview/ToeicLevelProgression";
import { ToeicProgressionSteps } from "./components/toeic-level-overview/ToeicProgressionSteps";
import {
	TOEIC_LEVELS,
	TOEIC_PROGRESSION_STEP_KEYS,
	toeicLevelOverviewKey,
} from "./components/toeic-level-overview/constants";

export const OnboardingToeicLevelOverviewScreen = () => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const levelNodes = useMemo<ToeicLevelNode[]>(
		() =>
			TOEIC_LEVELS.map(({ id, icon }) => ({
				id,
				icon,
				name: t(toeicLevelOverviewKey(`levels.${id}.name`)),
				band: t(toeicLevelOverviewKey(`levels.${id}.band`)),
			})),
		[t],
	);

	const levelDetails = useMemo<ToeicLevelDetail[]>(
		() =>
			TOEIC_LEVELS.map(({ id, scoreMin, scoreMax, exampleKeys }) => ({
				id,
				name: t(toeicLevelOverviewKey(`levelDetails.${id}.name`)),
				scoreRange: t(toeicLevelOverviewKey("levelDetails.scoreRange"), {
					band: t(toeicLevelOverviewKey(`levels.${id}.band`)),
					min: scoreMin,
					max: scoreMax,
				}),
				summary: t(toeicLevelOverviewKey(`levelDetails.${id}.summary`)),
				examples: exampleKeys.map((exampleKey) =>
					t(toeicLevelOverviewKey(`levelDetails.${id}.examples.${exampleKey}`)),
				),
			})),
		[t],
	);

	const progressionSteps = useMemo(
		() =>
			TOEIC_PROGRESSION_STEP_KEYS.map((stepKey) =>
				t(toeicLevelOverviewKey(`progression.${stepKey}`)),
			),
		[t],
	);

	return (
		<Box flex={1} gap={16}>
			<OnboardingBox gap={20}>
				<Box gap={14}>
					<Text variant="titleLarge">{t(toeicLevelOverviewKey("title"))}</Text>
					<Text variant="bodyMedium">
						{t(toeicLevelOverviewKey("description"))}
					</Text>
				</Box>

				<Box
					alignItems="center"
					justifyContent="center"
					paddingVertical={16}
					borderRadius={theme.roundness}
					backgroundColor={
						isMobile ? theme.colors.surface : theme.colors.background
					}
				>
					<ToeicLevelProgression
						levels={levelNodes}
						accessibilityLabel={t(toeicLevelOverviewKey("illustrationLabel"))}
					/>
				</Box>

				<ToeicProgressionSteps
					title={t(toeicLevelOverviewKey("progression.title"))}
					steps={progressionSteps}
				/>

				<ToeicLevelDetailsAccordion
					title={t(toeicLevelOverviewKey("levelDetails.title"))}
					levels={levelDetails}
				/>
			</OnboardingBox>

			<InsightCallout
				variant="flat"
				showLeadingIcon={false}
				showTrailingIcon={false}
				text={t(toeicLevelOverviewKey("disclaimer"))}
			/>
		</Box>
	);
};
